import React, { useState, useEffect } from 'react';
import {
  X,
  MapPin,
  User,
  Calendar,
  Clock,
  FileText,
  Phone,
  CheckCircle2,
  Zap,
  Euro,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon
} from 'lucide-react';
import { useTranslation } from '../translations';
import { 
  fetchLocations, 
  createInterventionRequest,
  fetchServicePricing,
  getCurrentUserCompany,
  type Location,
  type ServicePricing
} from '../lib/supabase';

interface DateOption {
  date: Date;
  formatted: string;
  dayName: string;
}

interface InterventionRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: string;
  serviceTitle: string;
  isUrgent?: boolean;
}

export function InterventionRequestModal({ 
  isOpen, 
  onClose, 
  serviceType, 
  serviceTitle,
  isUrgent = false 
}: InterventionRequestModalProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [servicePricing, setServicePricing] = useState<ServicePricing | null>(null);
  const [dateOptions, setDateOptions] = useState<DateOption[]>([]);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [selectedTimePreference, setSelectedTimePreference] = useState<'morning' | 'afternoon' | null>(null);

  const [formData, setFormData] = useState({
    locationType: 'existing', // 'existing' or 'manual'
    locationId: '',
    manualAddress: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    description: '',
    selectedDate: '',
    selectedTimePreference: '',
    urgentReason: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
      generateDateOptions();
    }
  }, [isOpen, serviceType]);

  const loadData = async () => {
    try {
      setIsLoadingLocations(true);
      
      // Get current company
      const company = await getCurrentUserCompany();
      if (!company) return;

      // Load locations and service pricing
      const [locationsData, pricingData] = await Promise.all([
        fetchLocations(company.id),
        fetchServicePricing(company.id)
      ]);
      
      setLocations(locationsData);
      
      // Find pricing for this service type
      const pricing = pricingData.find(p => p.service_type === serviceType);
      setServicePricing(pricing || null);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const generateDateOptions = () => {
    const options: DateOption[] = [];
    const today = new Date();
    
    // Generate date options for the next 14 days, skipping weekends
    for (let i = 0; options.length < 14; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      
      // Skip weekends (0 = Sunday, 6 = Saturday)
      const day = date.getDay();
      if (day === 0 || day === 6) continue;
      
      const formatted = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      
      options.push({ date, formatted, dayName });
    }
    
    setDateOptions(options);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Get current company
      const company = await getCurrentUserCompany();
      if (!company) {
        throw new Error('No company found');
      }

      // Calculate pricing
      const basePrice = servicePricing?.base_price || 0;
      const handlingFee = servicePricing?.handling_fee || 0;
      const totalCost = basePrice + handlingFee;

      // Format the selected time slot
      const selectedDate = dateOptions[currentDateIndex].formatted;
      const timePreference = selectedTimePreference === 'morning' ? 'Morning (9:00-12:00)' : 'Afternoon (13:00-17:00)';
      const timeSlot = `${dateOptions[currentDateIndex].dayName}, ${selectedDate} - ${timePreference}`;

      const requestData = {
        service_type: serviceType,
        service_title: serviceTitle,
        location_id: formData.locationType === 'existing' ? formData.locationId : null,
        manual_address: formData.locationType === 'manual' ? formData.manualAddress : null,
        contact_person: formData.contactPerson,
        contact_phone: formData.contactPhone,
        contact_email: formData.contactEmail || null,
        description: formData.description || null,
        is_urgent: isUrgent,
        urgent_reason: isUrgent ? formData.urgentReason : null,
        selected_time_slot: !isUrgent ? timeSlot : null,
        status: isUrgent ? 'action_needed' : 'scheduled',
        company_id: company.id,
        service_price: basePrice,
        handling_fee: handlingFee,
        total_cost: totalCost,
        invoiced: false,
        invoice_id: null
      };

      await createInterventionRequest(requestData);
      
      setIsSubmitted(true);
      setIsSubmitting(false);
      
      // Reset form after 3 seconds and close
      setTimeout(() => {
        setIsSubmitted(false);
        setCurrentStep(1);
        setFormData({
          locationType: 'existing',
          locationId: '',
          manualAddress: '',
          contactPerson: '',
          contactPhone: '',
          contactEmail: '',
          description: '',
          selectedDate: '',
          selectedTimePreference: '',
          urgentReason: ''
        });
        setSelectedTimePreference(null);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error creating intervention request:', error);
      setIsSubmitting(false);
    }
  };

  const handleUrgentSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Get current company
      const company = await getCurrentUserCompany();
      if (!company) {
        throw new Error('No company found');
      }

      // Calculate pricing with urgent surcharge
      const basePrice = servicePricing?.base_price || 0;
      const handlingFee = servicePricing?.handling_fee || 0;
      const urgentSurcharge = basePrice * 0.5; // 50% surcharge for urgent requests
      const totalCost = basePrice + handlingFee + urgentSurcharge;

      const requestData = {
        service_type: serviceType,
        service_title: serviceTitle,
        location_id: formData.locationType === 'existing' ? formData.locationId : null,
        manual_address: formData.locationType === 'manual' ? formData.manualAddress : null,
        contact_person: formData.contactPerson,
        contact_phone: formData.contactPhone,
        contact_email: formData.contactEmail || null,
        description: formData.description || null,
        is_urgent: true,
        urgent_reason: formData.urgentReason,
        selected_time_slot: null,
        status: 'action_needed',
        company_id: company.id,
        service_price: basePrice + urgentSurcharge,
        handling_fee: handlingFee,
        total_cost: totalCost,
        invoiced: false,
        invoice_id: null
      };

      await createInterventionRequest(requestData);
      
      // Simulate call center contact
      alert('Connecting you to our call center for urgent assistance...');
      
      setIsSubmitted(true);
      setIsSubmitting(false);
      
      setTimeout(() => {
        setIsSubmitted(false);
        setCurrentStep(1);
        setFormData({
          locationType: 'existing',
          locationId: '',
          manualAddress: '',
          contactPerson: '',
          contactPhone: '',
          contactEmail: '',
          description: '',
          selectedDate: '',
          selectedTimePreference: '',
          urgentReason: ''
        });
        setSelectedTimePreference(null);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error creating urgent intervention request:', error);
      setIsSubmitting(false);
    }
  };

  const nextStep = () => setCurrentStep(prev => prev + 1);
  const prevStep = () => setCurrentStep(prev => prev - 1);

  const canProceedStep1 = formData.locationType === 'existing' 
    ? formData.locationId 
    : formData.manualAddress;

  const canProceedStep2 = formData.contactPerson && formData.contactPhone;

  const canProceedStep3 = isUrgent 
    ? formData.urgentReason 
    : selectedTimePreference !== null;

  const handleNextDate = () => {
    if (currentDateIndex < dateOptions.length - 1) {
      setCurrentDateIndex(currentDateIndex + 1);
    }
  };

  const handlePrevDate = () => {
    if (currentDateIndex > 0) {
      setCurrentDateIndex(currentDateIndex - 1);
    }
  };

  if (!isOpen) return null;

  if (isSubmitted) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-8 text-center">
          <div className="w-16 h-16 gradient-bg rounded-full mx-auto mb-4 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {isUrgent ? 'Urgent Request Submitted' : 'Intervention Scheduled'}
          </h3>
          <p className="text-gray-600">
            {isUrgent 
              ? 'Your urgent request has been created and our call center will contact you shortly.'
              : 'Your intervention has been successfully scheduled and added to your bookings.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isUrgent ? 'Urgent Request' : 'Schedule Intervention'}
            </h2>
            <p className="text-gray-600 mt-1">{serviceTitle}</p>
            {servicePricing && (
              <div className="flex items-center gap-2 mt-2">
                <Euro className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  Base: €{servicePricing.base_price} + Fee: €{servicePricing.handling_fee}
                  {isUrgent && (
                    <span className="text-orange-600 ml-2">
                      + 50% urgent surcharge
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step 
                    ? 'bg-jaimy-red text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step ? 'bg-jaimy-red' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Location</span>
            <span>Contact</span>
            <span>{isUrgent ? 'Details' : 'Schedule'}</span>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Location */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Select Location
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="locationType"
                      value="existing"
                      checked={formData.locationType === 'existing'}
                      onChange={(e) => setFormData(prev => ({ ...prev, locationType: e.target.value }))}
                      className="text-jaimy-red focus:ring-jaimy-red"
                    />
                    <span className="text-gray-900 font-medium">Select from existing locations</span>
                  </label>
                </div>

                {formData.locationType === 'existing' && (
                  <div className="ml-6 space-y-2">
                    {isLoadingLocations ? (
                      <div className="text-center py-4">
                        <div className="w-6 h-6 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-gray-600">Loading locations...</p>
                      </div>
                    ) : locations.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-gray-600">No locations available. Please add a location first or use manual address.</p>
                      </div>
                    ) : (
                      locations.map((location) => (
                        <label key={location.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-jaimy-red transition-colors cursor-pointer">
                          <input
                            type="radio"
                            name="locationId"
                            value={location.id}
                            checked={formData.locationId === location.id}
                            onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
                            className="text-jaimy-red focus:ring-jaimy-red"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{location.name}</div>
                            <div className="text-sm text-gray-600">{location.address}</div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                )}

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="locationType"
                      value="manual"
                      checked={formData.locationType === 'manual'}
                      onChange={(e) => setFormData(prev => ({ ...prev, locationType: e.target.value }))}
                      className="text-jaimy-red focus:ring-jaimy-red"
                    />
                    <span className="text-gray-900 font-medium">Enter manual address</span>
                  </label>
                </div>

                {formData.locationType === 'manual' && (
                  <div className="ml-6">
                    <textarea
                      placeholder="Enter the full address..."
                      value={formData.manualAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, manualAddress: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Contact Person */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <User className="h-5 w-5" />
                Contact Person
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person Name *
                  </label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    placeholder="Enter contact person name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    placeholder="+32 123 456 789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address (Optional)
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    placeholder="Describe the work that needs to be done..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Schedule or Urgent Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {isUrgent ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Urgent Request Details
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-800 mb-2">
                      <Zap className="h-5 w-5" />
                      <span className="font-medium">Emergency Service</span>
                    </div>
                    <p className="text-red-700 text-sm">
                      This request will be processed immediately. Our call center will contact you within minutes to arrange urgent assistance.
                    </p>
                    {servicePricing && (
                      <div className="mt-3 p-3 bg-white rounded border">
                        <div className="text-sm">
                          <div className="flex justify-between">
                            <span>Base Price:</span>
                            <span>€{servicePricing.base_price}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Urgent Surcharge (50%):</span>
                            <span>€{(servicePricing.base_price * 0.5).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Handling Fee:</span>
                            <span>€{servicePricing.handling_fee}</span>
                          </div>
                          <div className="flex justify-between font-bold text-red-800 border-t pt-2 mt-2">
                            <span>Total Cost:</span>
                            <span>€{(servicePricing.base_price * 1.5 + servicePricing.handling_fee).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for Urgency *
                    </label>
                    <textarea
                      value={formData.urgentReason}
                      onChange={(e) => setFormData(prev => ({ ...prev, urgentReason: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      placeholder="Please describe why this is urgent and needs immediate attention..."
                    />
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Kies een voorkeurstijdstip
                  </h3>
                  {servicePricing && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-sm">
                        <div className="flex justify-between">
                          <span>Service Price:</span>
                          <span>€{servicePricing.base_price}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Handling Fee:</span>
                          <span>€{servicePricing.handling_fee}</span>
                        </div>
                        <div className="flex justify-between font-bold text-blue-800 border-t pt-2 mt-2">
                          <span>Total Cost:</span>
                          <span>€{(servicePricing.base_price + servicePricing.handling_fee).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Date Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={handlePrevDate}
                      disabled={currentDateIndex === 0}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    
                    <div className="text-center">
                      <h4 className="font-medium text-gray-900">
                        {dateOptions[currentDateIndex]?.dayName}, {dateOptions[currentDateIndex]?.formatted}
                      </h4>
                    </div>
                    
                    <button
                      onClick={handleNextDate}
                      disabled={currentDateIndex >= dateOptions.length - 1}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                  
                  {/* Time Preference Selection */}
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Select your preferred time of day. Our contact center will schedule the exact appointment time with the professional.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setSelectedTimePreference('morning')}
                        className={`flex flex-col items-center justify-center p-6 border rounded-lg transition-colors ${
                          selectedTimePreference === 'morning'
                            ? 'border-jaimy-red bg-jaimy-red/5 text-jaimy-red'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <Sun className={`h-8 w-8 mb-2 ${selectedTimePreference === 'morning' ? 'text-jaimy-red' : 'text-gray-500'}`} />
                        <span className="font-medium">Morning</span>
                        <span className="text-sm mt-1">9:00 - 12:00</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => setSelectedTimePreference('afternoon')}
                        className={`flex flex-col items-center justify-center p-6 border rounded-lg transition-colors ${
                          selectedTimePreference === 'afternoon'
                            ? 'border-jaimy-red bg-jaimy-red/5 text-jaimy-red'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <Moon className={`h-8 w-8 mb-2 ${selectedTimePreference === 'afternoon' ? 'text-jaimy-red' : 'text-gray-500'}`} />
                        <span className="font-medium">Afternoon</span>
                        <span className="text-sm mt-1">13:00 - 17:00</span>
                      </button>
                    </div>
                    
                    <p className="text-sm text-gray-500 mt-2">
                      Note: The exact appointment time will be confirmed by our contact center.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="p-6 border-t border-gray-100 flex justify-between">
          <button
            onClick={currentStep === 1 ? onClose : prevStep}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </button>

          {currentStep < 3 ? (
            <button
              onClick={nextStep}
              disabled={
                (currentStep === 1 && !canProceedStep1) ||
                (currentStep === 2 && !canProceedStep2)
              }
              className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={isUrgent ? handleUrgentSubmit : handleSubmit}
              disabled={!canProceedStep3 || isSubmitting}
              className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isUrgent ? 'Creating Request...' : 'Scheduling...'}
                </>
              ) : (
                <>
                  {isUrgent ? (
                    <>
                      <Phone className="h-4 w-4" />
                      Request Urgent Call
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Schedule Intervention
                    </>
                  )}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}